/**
 * Admin: System Dashboard (read-only analytics rollup)
 *
 * Wraps the `getSystemInsights` Function endpoint. See
 * ADMIN_PORTAL_UX_REQUIREMENTS.md §2.
 *
 * Note: the response shape is partially documented but flexible. We dig into
 * known paths defensively (everything is treated as optional).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { getAdminSystemInsights } from '../services/adminApi';
import {
  Card,
  CardTitle,
  CardDescription,
  StatGrid,
  Metric,
  Banner,
  RawJson,
  SecondaryButton,
  PrimaryButton,
  ButtonRow,
  SampleTable,
  Field,
  Label,
  InlineLabel,
} from '../components/admin/AdminUI';
import { displayEntityType } from '../utils/entityTerminology';

const TIMEFRAME_CHOICES = [7, 14, 30, 60, 90];
const AUTO_REFRESH_MS = 60_000;

type TopList = Array<{ city?: string; state?: string; eventCount?: number; count?: number }>;
type TypeDist = Array<{ type?: string; eventCount?: number; count?: number }>;

function n(v: unknown): number | null {
  return typeof v === 'number' ? v : null;
}

function pct(v: unknown): string {
  const num = n(v);
  return num == null ? '—' : `${num.toFixed(1)}%`;
}

function fmt(v: unknown): string {
  const num = n(v);
  return num == null ? '—' : num.toLocaleString();
}

function fmtBytes(v: unknown): string {
  const num = n(v);
  if (num == null) return '—';
  if (num >= 1024 * 1024 * 1024) return `${(num / (1024 ** 3)).toFixed(2)} GB`;
  if (num >= 1024 * 1024) return `${(num / (1024 ** 2)).toFixed(2)} MB`;
  if (num >= 1024) return `${(num / 1024).toFixed(1)} KB`;
  return `${num} B`;
}

const MONTH_LABELS = [
  '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function monthLabel(m: any): string {
  if (m?.label) return String(m.label);
  const year = m?.year;
  const month = m?.month;
  if (typeof month === 'number' && month >= 1 && month <= 12) {
    return year ? `${MONTH_LABELS[month]} ${year}` : MONTH_LABELS[month];
  }
  return month ? String(month) : '—';
}

export const AdminSystemDashboardPage: React.FC = () => {
  const [timeframe, setTimeframe] = useState<number>(30);
  const [includeDetails, setIncludeDetails] = useState<boolean>(false);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getAdminSystemInsights({ timeframe, includeDetails });
      setData(r);
      setFetchedAt(new Date());
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load system insights');
    } finally {
      setLoading(false);
    }
  }, [timeframe, includeDetails]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 60s while tab is focused.
  useEffect(() => {
    const tick = () => { if (!document.hidden) load(); };
    const id = window.setInterval(tick, AUTO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [load]);

  const insights = data?.insights ?? {};
  const stats = data?.systemStats ?? {};
  const details = data?.details ?? {};

  // New top-level blocks (per API_DOCUMENTATION.md §12 update):
  //   - database:     per-table row counts + storage usage
  //   - entities:     totals, breakdown by EntityType, events-per-entity
  //   - eventSources: totals, events-per-source
  const dbBlock: any = data?.database ?? null;
  const entitiesBlock: any = data?.entities ?? null;
  const eventSourcesBlock: any = data?.eventSources ?? null;

  // New `systemStats.db` enrichment sub-objects (per API_DOCUMENTATION.md §12).
  const locationsBlock: any = stats?.db?.locations ?? null;
  const venuesBlock: any = stats?.db?.venues ?? null;
  const enrichmentBlock: any = stats?.db?.enrichment ?? null;

  // Top-level geo / enrichmentInsights analytics blocks.
  const geoBlock: any = data?.geo ?? null;
  const enrichmentInsightsBlock: any = data?.enrichmentInsights ?? null;

  const totalEvents = stats?.eventInfo?.total ?? stats?.db?.eventInfo?.total;
  const totalEventInfoMap =
    stats?.eventInfoMap?.total ?? stats?.db?.eventInfoMap?.total;
  const eventsAddedTimeframe =
    stats?.db?.eventInfo?.added?.[`last${timeframe}Days`] ??
    stats?.eventInfo?.added?.[`last${timeframe}Days`];
  const mappingsAddedTimeframe =
    stats?.db?.eventInfoMap?.added?.[`last${timeframe}Days`] ??
    stats?.eventInfoMap?.added?.[`last${timeframe}Days`];

  const searchDocs =
    stats?.searchIndex?.documentCount ?? stats?.search?.documentCount;
  const searchStorageBytes =
    stats?.searchIndex?.storageSize ?? stats?.search?.storageSize;
  const searchErr = stats?.searchIndex?.error ?? stats?.search?.error;

  const activePartners = insights?.partners?.active;
  const upcoming = insights?.events?.upcoming;
  const virtualEvents = insights?.events?.virtual;
  const partnerHostedEvents = insights?.events?.partnerHosted;
  const virtualPct = insights?.events?.virtualPercentage;

  const recent = insights?.activity?.recentAdditions ?? {};
  const topCities: TopList = insights?.locations?.topCities ?? [];
  const topStates: TopList = insights?.locations?.topStates ?? [];

  const partnerPerformance: any[] = details?.partnerPerformance ?? [];
  const monthlyTrends: any[] = details?.monthlyTrends ?? [];
  const eventDuration: any = details?.eventDurationAnalysis ?? null;

  const top10Types = useMemo(() => {
    const typeDist: TypeDist = data?.insights?.eventTypes?.distribution ?? [];
    if (typeDist.length <= 10) return typeDist;
    const sorted = [...typeDist].sort(
      (a, b) => (b.eventCount ?? b.count ?? 0) - (a.eventCount ?? a.count ?? 0),
    );
    const head = sorted.slice(0, 10);
    const tail = sorted.slice(10);
    const otherCount = tail.reduce(
      (sum, t) => sum + (t.eventCount ?? t.count ?? 0),
      0,
    );
    return [...head, { type: 'Other', eventCount: otherCount }];
  }, [data]);

  return (
    <AdminLayout
      currentPage="systemInsights"
      pageTitle="System Dashboard"
      pageDescription="Database, search index, and recent activity at a glance."
    >
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <CardTitle>Filters</CardTitle>
            <CardDescription>
              Timeframe controls "upcoming" and "recent" rollups.
            </CardDescription>
          </div>
          <ButtonRow>
            <SecondaryButton onClick={load} disabled={loading}>
              <RefreshCw size={16} />
              Refresh
            </SecondaryButton>
          </ButtonRow>
        </div>

        <Field>
          <Label>Timeframe (days)</Label>
          <div>
            {TIMEFRAME_CHOICES.map((d) => (
              <InlineLabel key={d}>
                <input
                  type="radio"
                  name="timeframe"
                  checked={timeframe === d}
                  onChange={() => setTimeframe(d)}
                />
                {d}
              </InlineLabel>
            ))}
          </div>
        </Field>
        <Field>
          <InlineLabel>
            <input
              type="checkbox"
              checked={includeDetails}
              onChange={(e) => setIncludeDetails(e.target.checked)}
            />
            Show advanced details (entity performance, monthly trends, duration analysis)
          </InlineLabel>
        </Field>
        {fetchedAt && (
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>
            Last refreshed {fetchedAt.toLocaleTimeString()}
          </div>
        )}
      </Card>

      {error && <Banner tone="error">{error}</Banner>}

      <StatGrid>
        <Metric label="Total events" value={fmt(totalEvents)} />
        <Metric
          label="Total mappings"
          value={fmt(totalEventInfoMap)}
          hint="Rows in EventInfoMap"
        />
        <Metric
          label="Search index docs"
          value={fmt(searchDocs)}
          hint={
            searchErr ? (
              <span title={String(searchErr)}>⚠ search index unreachable</span>
            ) : searchStorageBytes != null ? (
              `${fmtBytes(searchStorageBytes)} storage`
            ) : undefined
          }
          tone={searchErr ? 'warning' : 'neutral'}
        />
        <Metric label="Active tech companies" value={fmt(activePartners)} />
        <Metric label={`Upcoming (next ${timeframe}d)`} value={fmt(upcoming)} />
        <Metric label="% Virtual upcoming" value={pct(virtualPct)} />
      </StatGrid>

      {(virtualEvents != null ||
        partnerHostedEvents != null ||
        eventsAddedTimeframe != null ||
        mappingsAddedTimeframe != null) && (
        <Card>
          <CardTitle>Upcoming events composition</CardTitle>
          <CardDescription>
            Breakdown of the {fmt(upcoming)} upcoming events plus rows added
            in the last {timeframe} days.
          </CardDescription>
          <StatGrid>
            <Metric label="Virtual" value={fmt(virtualEvents)} />
            <Metric label="Tech-company-hosted" value={fmt(partnerHostedEvents)} />
            <Metric
              label={`Events added (${timeframe}d)`}
              value={fmt(eventsAddedTimeframe)}
              tone="success"
            />
            <Metric
              label={`Mappings added (${timeframe}d)`}
              value={fmt(mappingsAddedTimeframe)}
            />
          </StatGrid>
        </Card>
      )}

      <Card>
        <CardTitle>Recent activity</CardTitle>
        <StatGrid>
          <Metric label="Last 24h" value={fmt(recent.last24Hours ?? recent.last1Day)} />
          <Metric label="Last 7d" value={fmt(recent.last7Days)} />
          <Metric label="Last 30d" value={fmt(recent.last30Days)} />
        </StatGrid>
      </Card>

      {dbBlock && (
        <Card>
          <CardTitle>Database storage</CardTitle>
          <CardDescription>
            Allocated size from <code>sp_spaceused</code> and per-table row
            counts from individual <code>COUNT_BIG(*)</code> queries.
          </CardDescription>
          <StatGrid>
            <Metric
              label="Total size"
              value={dbBlock.sizeMB != null ? `${Number(dbBlock.sizeMB).toLocaleString()} MB` : '—'}
              hint={dbBlock.sizeBytes != null ? `${Number(dbBlock.sizeBytes).toLocaleString()} bytes` : undefined}
            />
            <Metric
              label="Tables"
              value={Array.isArray(dbBlock.tables) ? dbBlock.tables.length : '—'}
            />
          </StatGrid>
          {Array.isArray(dbBlock.tables) && dbBlock.tables.length > 0 && (
            <SampleTable>
              <thead>
                <tr>
                  <th>Table</th>
                  <th style={{ textAlign: 'right' }}>Rows</th>
                </tr>
              </thead>
              <tbody>
                {[...dbBlock.tables]
                  .sort((a: any, b: any) => (b.rowCount ?? 0) - (a.rowCount ?? 0))
                  .map((t: any, i: number) => (
                    <tr key={i}>
                      <td><code>{t.name ?? '—'}</code></td>
                      <td style={{ textAlign: 'right' }}>
                        {t.error ? (
                          <span title={String(t.error)} style={{ color: '#cf1322' }}>
                            ⚠ error
                          </span>
                        ) : (
                          fmt(t.rowCount)
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </SampleTable>
          )}
          {dbBlock.error && (
            <Banner tone="warning">
              Storage metric error: {String(dbBlock.error)}
            </Banner>
          )}
        </Card>
      )}

      {entitiesBlock && (
        <Card>
          <CardTitle>Entities</CardTitle>
          <CardDescription>
            Totals and per-type breakdown from the <code>Entities</code> table.
          </CardDescription>
          <StatGrid>
            <Metric label="Total entities" value={fmt(entitiesBlock.total)} />
            <Metric
              label="Avg events / entity"
              value={
                entitiesBlock.eventsPerEntity?.avg != null
                  ? Number(entitiesBlock.eventsPerEntity.avg).toFixed(2)
                  : '—'
              }
            />
            <Metric label="Min" value={fmt(entitiesBlock.eventsPerEntity?.min)} />
            <Metric label="Max" value={fmt(entitiesBlock.eventsPerEntity?.max)} />
          </StatGrid>
          {Array.isArray(entitiesBlock.byType) && entitiesBlock.byType.length > 0 && (
            <>
              <Label>By type</Label>
              <SimpleBars
                items={entitiesBlock.byType.map((r: any) => ({
                  label: displayEntityType(r.type) || '—',
                  value: r.count ?? 0,
                }))}
              />
            </>
          )}
        </Card>
      )}

      {eventSourcesBlock && (
        <Card>
          <CardTitle>Event sources</CardTitle>
          <CardDescription>
            Distinct events reachable from each row in <code>EventSources</code>.
          </CardDescription>
          <StatGrid>
            <Metric label="Total sources" value={fmt(eventSourcesBlock.total)} />
            <Metric
              label="Avg events / source"
              value={
                eventSourcesBlock.eventsPerSource?.avg != null
                  ? Number(eventSourcesBlock.eventsPerSource.avg).toFixed(2)
                  : '—'
              }
            />
            <Metric label="Min" value={fmt(eventSourcesBlock.eventsPerSource?.min)} />
            <Metric label="Max" value={fmt(eventSourcesBlock.eventsPerSource?.max)} />
          </StatGrid>
        </Card>
      )}

      {locationsBlock && (
        <Card>
          <CardTitle>Locations &amp; geocoding</CardTitle>
          <CardDescription>
            Coverage of the <code>Location</code> dimension (city / state /
            country) and Azure Maps geocoding status.
          </CardDescription>
          <StatGrid>
            <Metric
              label="Total locations"
              value={fmt(locationsBlock.total)}
              hint="Distinct rows in the Location table"
            />
            <Metric
              label="% Geocoded"
              value={pct(locationsBlock.percentGeocoded)}
              tone={
                (locationsBlock.percentGeocoded ?? 0) >= 90
                  ? 'success'
                  : (locationsBlock.percentGeocoded ?? 0) >= 50
                  ? 'neutral'
                  : 'warning'
              }
            />
            <Metric
              label="Events with location"
              value={fmt(locationsBlock.eventsWithLocation)}
              hint="EventInfo.LocationId set"
            />
            <Metric
              label="Map-renderable"
              value={fmt(locationsBlock.eventsWithCoordinates)}
              hint="Has lat/long"
            />
          </StatGrid>
          {locationsBlock.byGeocodeStatus && (
            <>
              <Label>Geocode status breakdown</Label>
              <SimpleBars
                items={[
                  { label: 'success', value: locationsBlock.byGeocodeStatus.success ?? 0 },
                  { label: 'pending', value: locationsBlock.byGeocodeStatus.pending ?? 0 },
                  { label: 'skipped', value: locationsBlock.byGeocodeStatus.skipped ?? 0 },
                  { label: 'failed',  value: locationsBlock.byGeocodeStatus.failed  ?? 0 },
                ]}
              />
            </>
          )}
        </Card>
      )}

      {venuesBlock && (
        <Card>
          <CardTitle>Venues</CardTitle>
          <CardDescription>
            Coverage of the <code>Venue</code> dimension table.
          </CardDescription>
          <StatGrid>
            <Metric label="Total venues" value={fmt(venuesBlock.total)} />
            <Metric
              label="With street address"
              value={fmt(venuesBlock.withStreetAddress)}
              hint={
                venuesBlock.total
                  ? `${Math.round(((venuesBlock.withStreetAddress ?? 0) / venuesBlock.total) * 100)}%`
                  : undefined
              }
            />
            <Metric
              label="Events with venue"
              value={fmt(venuesBlock.eventsWithVenue)}
              hint="EventInfo.VenueId set"
            />
            <Metric
              label="Distinct venues used"
              value={fmt(venuesBlock.distinctVenuesUsed)}
            />
          </StatGrid>
        </Card>
      )}

      {enrichmentBlock && (
        <Card>
          <CardTitle>Enrichment coverage</CardTitle>
          <CardDescription>
            Share of events for which the AI extractor surfaced each
            optional field. Coverage % uses{' '}
            <code>{fmt(enrichmentBlock.totalEvents)}</code> events as the
            denominator.
          </CardDescription>
          <StatGrid>
            <Metric label="Free events" value={fmt(enrichmentBlock.freeEvents)} hint={pct(enrichmentBlock.coveragePercent?.freeEvents)} />
            <Metric label="Paid events" value={fmt(enrichmentBlock.paidEvents)} hint={pct(enrichmentBlock.coveragePercent?.paidEvents)} />
            <Metric label="With price" value={fmt(enrichmentBlock.withPrice)} hint={pct(enrichmentBlock.coveragePercent?.withPrice)} />
            <Metric label="With capacity" value={fmt(enrichmentBlock.withCapacity)} hint={pct(enrichmentBlock.coveragePercent?.withCapacity)} />
          </StatGrid>
          <Label>Coverage by field</Label>
          <SimpleBars
            items={[
              { label: 'audienceLevel',        value: enrichmentBlock.coveragePercent?.withAudienceLevel ?? 0 },
              { label: 'registrationDeadline', value: enrichmentBlock.coveragePercent?.withRegistrationDeadline ?? 0 },
              { label: 'agendaSummary',        value: enrichmentBlock.coveragePercent?.withAgendaSummary ?? 0 },
              { label: 'speakers',             value: enrichmentBlock.coveragePercent?.withSpeakers ?? 0 },
              { label: 'topics',               value: enrichmentBlock.coveragePercent?.withTopics ?? 0 },
              { label: 'industries',           value: enrichmentBlock.coveragePercent?.withIndustries ?? 0 },
              { label: 'targetRoles',          value: enrichmentBlock.coveragePercent?.withTargetRoles ?? 0 },
              { label: 'price',                value: enrichmentBlock.coveragePercent?.withPrice ?? 0 },
              { label: 'capacity',             value: enrichmentBlock.coveragePercent?.withCapacity ?? 0 },
            ]}
          />
        </Card>
      )}

      {geoBlock && (
        <Card>
          <CardTitle>Geo analytics</CardTitle>
          <CardDescription>
            Top countries / cities computed from successfully-geocoded
            <code> Location</code> rows, plus Azure Maps throughput.
          </CardDescription>
          <StatGrid>
            <Metric
              label="Geocoded — last 7d"
              value={fmt(geoBlock.recentSuccessLast7Days)}
              tone="success"
            />
            <Metric
              label="Failed — last 7d"
              value={fmt(geoBlock.recentFailedLast7Days)}
              tone={
                (geoBlock.recentFailedLast7Days ?? 0) > 0 ? 'warning' : 'neutral'
              }
            />
            <Metric
              label="Providers"
              value={
                Array.isArray(geoBlock.providers) && geoBlock.providers.length > 0
                  ? geoBlock.providers.map((p: any) => p.provider).join(', ')
                  : '—'
              }
            />
          </StatGrid>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <div>
              <Label>Top countries</Label>
              <SimpleBars
                items={(geoBlock.topCountries ?? []).slice(0, 10).map((r: any) => ({
                  label: r.country ?? '—',
                  value: r.eventCount ?? 0,
                }))}
              />
            </div>
            <div>
              <Label>Top geocoded cities</Label>
              <SimpleBars
                items={(geoBlock.topGeocodedCities ?? []).slice(0, 10).map((r: any) => ({
                  label: r.state ? `${r.city ?? '—'}, ${r.state}` : (r.city ?? '—'),
                  value: r.eventCount ?? 0,
                }))}
              />
            </div>
          </div>
          {Array.isArray(geoBlock.geocodeErrors) && geoBlock.geocodeErrors.length > 0 && (
            <>
              <Label>Top geocode errors</Label>
              <SampleTable>
                <thead>
                  <tr>
                    <th>Error</th>
                    <th style={{ textAlign: 'right' }}>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {geoBlock.geocodeErrors.slice(0, 10).map((e: any, i: number) => (
                    <tr key={i}>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {e.error ?? '—'}
                      </td>
                      <td style={{ textAlign: 'right' }}>{fmt(e.count)}</td>
                    </tr>
                  ))}
                </tbody>
              </SampleTable>
            </>
          )}
        </Card>
      )}

      {enrichmentInsightsBlock && (
        <Card>
          <CardTitle>Enrichment insights</CardTitle>
          <CardDescription>
            Aggregates over the AI-enriched event fields (price, capacity,
            audience, topics, industries, roles).
          </CardDescription>
          {enrichmentInsightsBlock.priceSummary && (
            <>
              <Label>Price summary</Label>
              <StatGrid>
                <Metric
                  label="Avg"
                  value={
                    enrichmentInsightsBlock.priceSummary.avg != null
                      ? `$${Number(enrichmentInsightsBlock.priceSummary.avg).toFixed(2)}`
                      : '—'
                  }
                />
                <Metric
                  label="Min"
                  value={
                    enrichmentInsightsBlock.priceSummary.min != null
                      ? `$${Number(enrichmentInsightsBlock.priceSummary.min).toFixed(2)}`
                      : '—'
                  }
                />
                <Metric
                  label="Max"
                  value={
                    enrichmentInsightsBlock.priceSummary.max != null
                      ? `$${Number(enrichmentInsightsBlock.priceSummary.max).toFixed(2)}`
                      : '—'
                  }
                />
                <Metric
                  label="Top currency"
                  value={
                    enrichmentInsightsBlock.priceSummary.currencyTop?.[0]?.currency ?? '—'
                  }
                  hint={
                    enrichmentInsightsBlock.priceSummary.currencyTop?.[0]?.count != null
                      ? `${fmt(enrichmentInsightsBlock.priceSummary.currencyTop[0].count)} events`
                      : undefined
                  }
                />
              </StatGrid>
            </>
          )}
          {enrichmentInsightsBlock.capacitySummary && (
            <>
              <Label>Capacity summary</Label>
              <StatGrid>
                <Metric
                  label="Avg"
                  value={
                    enrichmentInsightsBlock.capacitySummary.avg != null
                      ? Number(enrichmentInsightsBlock.capacitySummary.avg).toFixed(0)
                      : '—'
                  }
                />
                <Metric label="Min" value={fmt(enrichmentInsightsBlock.capacitySummary.min)} />
                <Metric label="Max" value={fmt(enrichmentInsightsBlock.capacitySummary.max)} />
              </StatGrid>
            </>
          )}
          {Array.isArray(enrichmentInsightsBlock.audienceLevelDist) && enrichmentInsightsBlock.audienceLevelDist.length > 0 && (
            <>
              <Label>Audience level</Label>
              <SimpleBars
                items={enrichmentInsightsBlock.audienceLevelDist.map((r: any) => ({
                  label: r.audienceLevel ?? '—',
                  value: r.count ?? 0,
                }))}
              />
            </>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
            {Array.isArray(enrichmentInsightsBlock.topTopics) && enrichmentInsightsBlock.topTopics.length > 0 && (
              <div>
                <Label>Top topics</Label>
                <SimpleBars
                  items={enrichmentInsightsBlock.topTopics.slice(0, 10).map((r: any) => ({
                    label: r.topic ?? '—',
                    value: r.count ?? 0,
                  }))}
                />
              </div>
            )}
            {Array.isArray(enrichmentInsightsBlock.topIndustries) && enrichmentInsightsBlock.topIndustries.length > 0 && (
              <div>
                <Label>Top industries</Label>
                <SimpleBars
                  items={enrichmentInsightsBlock.topIndustries.slice(0, 10).map((r: any) => ({
                    label: r.industry ?? '—',
                    value: r.count ?? 0,
                  }))}
                />
              </div>
            )}
            {Array.isArray(enrichmentInsightsBlock.topTargetRoles) && enrichmentInsightsBlock.topTargetRoles.length > 0 && (
              <div>
                <Label>Top target roles</Label>
                <SimpleBars
                  items={enrichmentInsightsBlock.topTargetRoles.slice(0, 10).map((r: any) => ({
                    label: r.role ?? '—',
                    value: r.count ?? 0,
                  }))}
                />
              </div>
            )}
          </div>
        </Card>
      )}

      <Card>
        <CardTitle>Top locations</CardTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <div>
            <Label>Top cities</Label>
            <SimpleBars items={topCities.slice(0, 10).map((r) => ({
              label: r.city ?? '—',
              value: r.eventCount ?? r.count ?? 0,
            }))} />
          </div>
          <div>
            <Label>Top states</Label>
            <SimpleBars items={topStates.slice(0, 10).map((r) => ({
              label: r.state ?? '—',
              value: r.eventCount ?? r.count ?? 0,
            }))} />
          </div>
        </div>
      </Card>

      <Card>
        <CardTitle>Event type distribution</CardTitle>
        <SimpleBars items={top10Types.map((t) => ({
          label: t.type ?? '—',
          value: t.eventCount ?? t.count ?? 0,
        }))} />
      </Card>

      {includeDetails && partnerPerformance.length > 0 && (
        <Card>
          <CardTitle>Entity performance</CardTitle>
          <SampleTable>
            <thead>
              <tr>
                <th>Entity</th>
                <th>Total events</th>
                <th>Upcoming</th>
                <th>Latest</th>
              </tr>
            </thead>
            <tbody>
              {partnerPerformance.slice(0, 25).map((p, i) => (
                <tr key={i}>
                  <td>{p.entityName ?? p.name ?? '—'}</td>
                  <td>{fmt(p.totalEvents ?? p.eventCount)}</td>
                  <td>{fmt(p.upcomingEvents)}</td>
                  <td>{p.latestEventDate ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </SampleTable>
        </Card>
      )}

      {includeDetails && monthlyTrends.length > 0 && (
        <Card>
          <CardTitle>Monthly trends (last 12 months)</CardTitle>
          <SampleTable>
            <thead>
              <tr>
                <th>Month</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ textAlign: 'right' }}>Virtual</th>
                <th style={{ textAlign: 'right' }}>Tech-company-hosted</th>
              </tr>
            </thead>
            <tbody>
              {monthlyTrends.map((m, i) => (
                <tr key={i}>
                  <td>{monthLabel(m)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {fmt(m.eventCount ?? m.total ?? m.count)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {fmt(m.virtualCount ?? m.virtual)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {fmt(m.partnerHostedCount ?? m.partnerHosted)}
                  </td>
                </tr>
              ))}
            </tbody>
          </SampleTable>
        </Card>
      )}

      {includeDetails && eventDuration && (
        <Card>
          <CardTitle>Event duration analysis</CardTitle>
          <CardDescription>
            Distribution of event durations across the catalogue.
          </CardDescription>
          <StatGrid>
            <Metric
              label="Avg duration"
              value={
                eventDuration.averageDurationHours != null
                  ? `${Number(eventDuration.averageDurationHours).toFixed(1)} h`
                  : '—'
              }
            />
            <Metric
              label="Min"
              value={
                eventDuration.minDurationHours != null
                  ? `${eventDuration.minDurationHours} h`
                  : '—'
              }
            />
            <Metric
              label="Max"
              value={
                eventDuration.maxDurationHours != null
                  ? `${eventDuration.maxDurationHours} h`
                  : '—'
              }
            />
            <Metric
              label="Short (<2h)"
              value={fmt(eventDuration.distribution?.shortEvents)}
            />
            <Metric
              label="Medium (2–8h)"
              value={fmt(eventDuration.distribution?.mediumEvents)}
            />
            <Metric
              label="Long (>8h)"
              value={fmt(eventDuration.distribution?.longEvents)}
            />
          </StatGrid>
          {eventDuration.distribution && (
            <SimpleBars
              items={[
                { label: 'Short (<2h)', value: eventDuration.distribution.shortEvents ?? 0 },
                { label: 'Medium (2–8h)', value: eventDuration.distribution.mediumEvents ?? 0 },
                { label: 'Long (>8h)', value: eventDuration.distribution.longEvents ?? 0 },
              ]}
            />
          )}
        </Card>
      )}

      {data && (
        <Card>
          <CardTitle>Raw response</CardTitle>
          <RawJson data={data} label="View JSON" />
          <ButtonRow>
            <PrimaryButton onClick={load} disabled={loading}>
              <RefreshCw size={16} />
              Re-fetch
            </PrimaryButton>
          </ButtonRow>
        </Card>
      )}
    </AdminLayout>
  );
};

// ---------------------------------------------------------------------------
// Lightweight bar chart (no external chart lib dependency).
// ---------------------------------------------------------------------------

const SimpleBars: React.FC<{ items: Array<{ label: string; value: number }> }> = ({ items }) => {
  if (items.length === 0) return <div style={{ fontSize: 13, color: '#8c8c8c' }}>No data.</div>;
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
      {items.map((it, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '160px 1fr 60px', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.label}>
            {it.label}
          </div>
          <div style={{ background: '#f0f0f0', borderRadius: 4, height: 14 }}>
            <div
              style={{
                width: `${(it.value / max) * 100}%`,
                background: '#1890ff',
                height: '100%',
                borderRadius: 4,
              }}
            />
          </div>
          <div style={{ textAlign: 'right', color: '#595959' }}>{it.value.toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
};

export default AdminSystemDashboardPage;
